import {
    ApplicationRef,
    ComponentRef,
    Injectable,
    ViewContainerRef,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Dialog } from './dialog.component';
import { DIALOG_FADE_MS } from './dialog.animation';
import { v4 as uuidv4 } from 'uuid';

/**
 * Options for showing a dialog.
 */
export type DialogShowOptions = {
    component: any;
    width?: string | null;
    height?: string | null;
    minWidth?: string | null;
    minHeight?: string | null;
    maxWidth?: string | null;
    maxHeight?: string | null;
    escapeCloses?: boolean | null;
    inputs?: any | null;
    data?: any | null;
};

/**
 * Represents the information of a dialog instance.
 * It contains an id that can be used to identify the dialog.
 */
export class DialogInfo {
    constructor(
        public readonly id: string | null = null,
        public readonly data: any | null = null,
    ) {}
}

/**
 * Represents a reference to a dialog instance.
 * It provides methods to control the dialog's visibility and access its properties.
 *
 */
export class DialogRef {
    public readonly id: string;
    private readonly dialogInstance: Dialog;
    private readonly subjectAfterOpened = new Subject<void>();
    private readonly subjectAfterClosed = new Subject<any>();
    private readonly afterClosed$ = this.subjectAfterClosed.asObservable();
    private readonly afterOpened$ = this.subjectAfterOpened.asObservable();

    constructor(
        private readonly componentRef: ComponentRef<Dialog>,
        public readonly data: any | null = null,
    ) {
        this.id = uuidv4();
        this.dialogInstance = this.componentRef.instance;
        this.dialogInstance.setDialogRef(this);
    }

    public setEscapeCloses(escapeCloses: boolean | null): void {
        this.dialogInstance.escapeCloses.set(escapeCloses ?? true);
    }

    public setInputs(inputs: any | null = null): void {
        this.dialogInstance.setInputs(inputs ?? []);
    }

    public setWidth(width: string | null): void {
        this.dialogInstance.width.set(width || 'auto');
    }

    public setHeight(height: string | null): void {
        this.dialogInstance.height.set(height || 'auto');
    }

    public setMinWidth(width: string | null): void {
        this.dialogInstance.minWidth.set(width || 'auto');
    }

    public setMinHeight(height: string | null): void {
        this.dialogInstance.minHeight.set(height || 'auto');
    }

    public setMaxWidth(width: string | null): void {
        this.dialogInstance.maxWidth.set(width || 'auto');
    }

    public setMaxHeight(height: string | null): void {
        this.dialogInstance.maxHeight.set(height || 'auto');
    }

    public show(): void {
        this.dialogInstance.show();
        this.subjectAfterOpened.next();
    }

    /**
     * Internal method.
     * DO NOT USE IT DIRECTLY. Instead, call DialogManager.close(id).
     *
     * Hides the dialog but doesn't notify subscribers.
     *
     */
    public hide(): void {
        this.dialogInstance.hide();
    }

    /**
     * Internal method.
     * DO NOT USE IT DIRECTLY.
     *
     * Destroys the dialog instance and its component reference.
     *
     */
    public destroy(): void {
        this.componentRef.destroy();
    }

    /**
     * Internal method.
     * DO NOT USE IT DIRECTLY.
     *
     * Notify all subscribers but doesn't hide or closes
     * the dialog.
     */
    public notifyClosed(status: any | null = null): void {
        this.subjectAfterClosed.next(status);
    }

    public setComponent(component: any): void {
        this.dialogInstance.setComponent(component);
    }

    public afterClosed(): Observable<any> {
        return this.afterClosed$;
    }

    public afterOpened(): Observable<any> {
        return this.afterOpened$;
    }
}

/**
 * Gerencia a exibição e o ciclo de vida de diálogos.
 *
 */
@Injectable({ providedIn: 'root' })
export class DialogManager {
    private afterAllClosedSubject = new Subject<void>();
    private afterOpenedSubject = new Subject<DialogRef>();
    private afterAllClosed$ = this.afterAllClosedSubject.asObservable();
    private afterOpened$ = this.afterOpenedSubject.asObservable();
    private rootViewContainerRef: ViewContainerRef | null = null;
    private dialogRefs: DialogRef[] = [];

    constructor(private applicationRef: ApplicationRef) {}

    /**
     * Finds an open dialog by its id.
     *
     * @param id - The id of the dialog to be retrieved.
     * @returns The DialogRef object if found, otherwise undefined.
     */
    public getDialogById(id: string): DialogRef | undefined {
        return this.dialogRefs.find((dialogRef) => dialogRef.id === id);
    }

    /**
     * Stream that emits when all open dialog have finished closing.
     * Will emit on subscribe if there are no open dialogs to begin with.
     *
     * @returns An observable that emits when all dialogs are closed.
     */
    public afterAllClosed(): Observable<void> {
        return this.afterAllClosed$;
    }

    /**
     * Stream that emits when a dialog has been opened.
     *
     * @returns An observable that emits when a dialog is opened.
     */
    public afterOpened(): Observable<DialogRef> {
        return this.afterOpened$;
    }

    /**
     * Closes a dialog by its id.
     *
     * @param id - The id of the dialog to be closed.
     * @param status - The status to be passed to the dialog when closing.
     */
    public close(id: string, status: any | null = null): void {
        const dialogRef = this.getDialogById(id);

        if (dialogRef) {
            // `hide()` dispara o fade-out (:leave). O resultado é notificado na
            // hora; a destruição do componente é adiada até o fim do fade para
            // que a animação de saída seja exibida.
            dialogRef.hide();
            dialogRef.notifyClosed(status);

            this.dialogRefs = this.dialogRefs.filter((ref) => ref.id !== id);

            setTimeout(() => dialogRef.destroy(), DIALOG_FADE_MS);
        }

        if (this.dialogRefs.length <= 0) {
            this.afterAllClosedSubject.next();
        }
    }

    /**
     * Closes all of the currently-open dialogs.
     *
     */
    public closeAll(): void {
        this.dialogRefs.forEach((dialogRef) => {
            this.close(dialogRef.id);
        });

        this.dialogRefs = [];
    }

    /**
     * Creates and shows a dialog with the specified options.
     *
     * @param options - The options for the dialog, including the component to be displayed.
     * @returns A reference to the created dialog instance.
     */
    public show(options: DialogShowOptions): DialogRef {
        if (!options.component) {
            throw new Error('Component is required to show dialog.');
        }

        if (this.rootViewContainerRef == null) {
            this.rootViewContainerRef =
                this.applicationRef.components[0].injector.get(
                    ViewContainerRef,
                );
        }

        const componentRef = this.rootViewContainerRef.createComponent(Dialog);
        const dialogRef = new DialogRef(componentRef, options.data);

        dialogRef.setInputs(options.inputs);

        if (options.maxHeight) {
            dialogRef.setMaxHeight(options.maxHeight);
        }

        if (options.maxWidth) {
            dialogRef.setMaxWidth(options.maxWidth);
        }

        if (options.minWidth) {
            dialogRef.setMinWidth(options.minWidth);
        }

        dialogRef.setMinHeight('250px');

        dialogRef.setEscapeCloses(options.escapeCloses ?? true);
        dialogRef.setWidth(options.width || '600px');
        dialogRef.setHeight(options.height || '250px');
        dialogRef.setComponent(options.component);
        dialogRef.show();

        this.dialogRefs.push(dialogRef);

        return dialogRef;
    }
}
